import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { usePageEvents } from "./usePageEvents";

const SESSION_KEY = "visitor_session_id";
const CONSENT_KEY = "cookie_consent";

const generateSessionId = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return "mobile";
  }
  return "desktop";
};

const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("SamsungBrowser")) return "Samsung Browser";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  if (ua.includes("Edg")) return "Edge";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  return "Unknown";
};

const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Windows")) return "Windows";
  if (ua.includes("Mac")) return "macOS";
  if (ua.includes("Linux")) return "Linux";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("iOS") || ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  return "Unknown";
};

const getConsentLevel = (): string => {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return "none";
    
    const parsed = JSON.parse(consent);
    if (parsed.marketing) return "marketing";
    if (parsed.analytics) return "analytics";
    if (parsed.necessary) return "necessary";
    return "none";
  } catch {
    return "none";
  }
};

const hasAnalyticsConsent = (): boolean => {
  try {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (!consent) return false;
    
    const parsed = JSON.parse(consent);
    return parsed.analytics === true;
  } catch {
    return false;
  }
};

// Parse UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || null,
    utm_medium: params.get('utm_medium') || null,
    utm_campaign: params.get('utm_campaign') || null,
    utm_term: params.get('utm_term') || null,
    utm_content: params.get('utm_content') || null,
  };
};

interface GeoData {
  ip: string;
  ip_hash: string;
  hostname: string;
  country: string;
  country_code: string;
  region: string;
  city: string;
  timezone: string;
  isp: string;
  org: string;
}

// Track analytics via rate-limited edge function
const trackAnalytics = async (type: string, data: Record<string, unknown>) => {
  try {
    const { data: response, error } = await supabase.functions.invoke('track-analytics', {
      body: { type, data },
    });
    
    if (error) {
      console.error('Error tracking analytics:', error);
      return false;
    }
    
    if (response?.limited) {
      console.log('Analytics rate limited');
      return false;
    }
    
    return response?.success || false;
  } catch (error) {
    console.error('Error tracking analytics:', error);
    return false;
  }
};

export const usePageTracking = () => {
  const location = useLocation();
  const sessionStartTime = useRef<number>(Date.now());
  const lastPath = useRef<string>("");
  const geoDataRef = useRef<GeoData | null>(null);
  const { getScrollDepth, getTimeOnPage } = usePageEvents();

  // Fetch geo data from edge function
  const fetchGeoData = useCallback(async (): Promise<GeoData | null> => {
    if (geoDataRef.current) return geoDataRef.current;

    try {
      const { data, error } = await supabase.functions.invoke('track-visitor');
      
      if (error) {
        console.error('Error fetching geo data:', error);
        return null;
      }

      geoDataRef.current = data as GeoData;
      return data as GeoData;
    } catch (error) {
      console.error('Error fetching geo data:', error);
      return null;
    }
  }, []);

  useEffect(() => {
    const trackPageView = async () => {
      // Only track if analytics consent is given
      if (!hasAnalyticsConsent()) {
        return;
      }

      // Get or create session ID
      let sessionId = sessionStorage.getItem(SESSION_KEY);
      const isNewSession = !sessionId;
      
      if (!sessionId) {
        sessionId = generateSessionId();
        sessionStorage.setItem(SESSION_KEY, sessionId);
        sessionStartTime.current = Date.now();
      }

      // Fetch geo data
      const geoData = await fetchGeoData();
      
      // Get UTM parameters
      const utmParams = getUTMParams();

      const pageData = {
        page_path: location.pathname,
        page_title: document.title,
        referrer: document.referrer || null,
        user_agent: navigator.userAgent,
        session_id: sessionId,
        device_type: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        language: navigator.language,
        consent_level: getConsentLevel(),
        // Geo fields
        ip_address_encrypted: geoData?.ip || null,
        ip_hash: geoData?.ip_hash || null,
        hostname: geoData?.hostname || null,
        country: geoData?.country || null,
        city: geoData?.city || null,
        region: geoData?.region || null,
        isp: geoData?.isp || null,
        timezone: geoData?.timezone || null,
        // UTM params
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_term: utmParams.utm_term,
        utm_content: utmParams.utm_content,
        // Engagement metrics (will be updated on exit)
        scroll_depth: 0,
        time_on_page: 0,
        is_bounce: true,
        exit_page: false,
      };

      try {
        // Insert page view via rate-limited edge function
        await trackAnalytics('page_view', pageData);

        // Update or create session
        if (isNewSession) {
          await trackAnalytics('session_create', {
            session_id: sessionId,
            entry_page: location.pathname,
            exit_page: location.pathname,
            referrer: document.referrer || null,
            device_type: getDeviceType(),
            browser: getBrowser(),
            os: getOS(),
            consent_level: getConsentLevel(),
            // Geo fields
            ip_hash: geoData?.ip_hash || null,
            hostname: geoData?.hostname || null,
            country: geoData?.country || null,
            city: geoData?.city || null,
            region: geoData?.region || null,
            isp: geoData?.isp || null,
            is_returning: false,
            utm_source: utmParams.utm_source,
            utm_medium: utmParams.utm_medium,
            utm_campaign: utmParams.utm_campaign,
          });
        } else {
          const durationSeconds = Math.floor((Date.now() - sessionStartTime.current) / 1000);
          
          // Get current session data
          const { data: session } = await supabase
            .from("visitor_sessions")
            .select("page_count")
            .eq("session_id", sessionId)
            .single();
          
          await trackAnalytics('session_update', {
            session_id: sessionId,
            exit_page: location.pathname,
            duration_seconds: durationSeconds,
            page_count: (session?.page_count || 0) + 1,
            total_time_on_site: durationSeconds,
          });

          // Mark previous page as not exit page and update engagement
          if (lastPath.current) {
            await trackAnalytics('page_view_update', {
              session_id: sessionId,
              page_path: lastPath.current,
              exit_page: false,
              is_bounce: false,
              scroll_depth: getScrollDepth(),
              time_on_page: getTimeOnPage(),
            });
          }
        }
      } catch (error) {
        console.error("Error tracking page view:", error);
      }

      lastPath.current = location.pathname;
    };

    trackPageView();

    // Listen for consent updates
    const handleConsentUpdate = () => {
      if (hasAnalyticsConsent() && lastPath.current !== location.pathname) {
        trackPageView();
      }
    };

    window.addEventListener("cookieConsentUpdated", handleConsentUpdate);
    return () => window.removeEventListener("cookieConsentUpdated", handleConsentUpdate);
  }, [location.pathname, fetchGeoData, getScrollDepth, getTimeOnPage]);
};

export default usePageTracking;
